import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ShieldCheck,
  LogOut,
  Bell,
  Search,
  Menu,
  X,
  ChevronRight,
  Star,
  Eye,
  Settings,
  HelpCircle,
  Flame,
  CheckCircle2,
  KeyRound,
  Lock,
  EyeOff,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import {
  fetchSystemMode,
  updateSystemMode,
  touchAdminActivity,
  isAdminSessionInactive,
  clearAdminSession,
  logoutAdminApi,
} from '../utils/api';
import { getAdminEmail, changeAdminPassword } from '../utils/auth';

const NAV_ITEMS = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/users',         icon: Users,           label: 'Users' },
  { to: '/kyc',           icon: ShieldCheck,     label: 'KYC Queue', badge: '5' },
  { to: '/moderation',    icon: Eye,             label: 'Content Moderation', badge: '3' },
  { to: '/subscriptions', icon: Star,            label: 'Subscriptions' },
  { to: '/payments',      icon: CreditCard,      label: 'Payments' },
];

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [systemMode, setSystemMode] = useState<'test' | 'live'>('live');
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(3);
  const navigate = useNavigate();

  // ── Change Password Modal State ──
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [changePassError, setChangePassError] = useState('');
  const [changePassSuccess, setChangePassSuccess] = useState('');
  const [changePassLoading, setChangePassLoading] = useState(false);

  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/users?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    fetchSystemMode().then((res) => {
      setSystemMode(res.mode);
    });

    const handleModeEvent = (e: any) => {
      if (e.detail?.mode) {
        setSystemMode(e.detail.mode);
      }
    };
    window.addEventListener('system-mode-changed', handleModeEvent);

    // ── Inactivity Detection & Touch Listeners (5-Minute Inactivity Window) ──
    let lastThrottledTouch = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastThrottledTouch > 4000) {
        lastThrottledTouch = now;
        touchAdminActivity();
      }
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));

    // Periodic check every 5 seconds for 5-minute inactivity expiration
    const inactivityTimer = setInterval(() => {
      if (isAdminSessionInactive()) {
        clearAdminSession('inactivity');
        navigate('/login?reason=inactivity');
      }
    }, 5000);

    const handleSessionExpired = (e: any) => {
      const reason = e?.detail?.reason || 'inactivity';
      navigate(`/login?reason=${reason}`);
    };
    window.addEventListener('admin-session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('system-mode-changed', handleModeEvent);
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
      clearInterval(inactivityTimer);
      window.removeEventListener('admin-session-expired', handleSessionExpired);
    };
  }, [navigate]);

  const handleToggleMode = async (targetMode: 'test' | 'live') => {
    if (systemMode === targetMode || isUpdatingMode) return;
    setIsUpdatingMode(true);
    try {
      const res = await updateSystemMode(targetMode);
      setSystemMode(res.mode);
      window.dispatchEvent(new CustomEvent('system-mode-changed', { detail: { mode: res.mode } }));
      setToastMessage(
        res.mode === 'live'
          ? 'Switched to Live Production Mode (Connected to Cloudflare D1)'
          : 'Switched to Test / Simulation Sandbox'
      );
      setTimeout(() => setToastMessage(null), 3500);
    } catch (e) {
      console.error('Failed to update system mode:', e);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const handleLogout = async () => {
    await logoutAdminApi();
    navigate('/login');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePassError('');
    setChangePassSuccess('');

    if (!currentPass || !newPass || !confirmPass) {
      setChangePassError('Please fill out all password fields.');
      return;
    }
    if (newPass.length < 6) {
      setChangePassError('New password must be at least 6 characters long.');
      return;
    }
    if (newPass !== confirmPass) {
      setChangePassError('New passwords do not match. Please verify.');
      return;
    }

    setChangePassLoading(true);
    await new Promise((r) => setTimeout(r, 600));

    const res = changeAdminPassword(currentPass, newPass);
    setChangePassLoading(false);

    if (res.success) {
      setChangePassSuccess(res.message);
      setCurrentPass('');
      setNewPass('');
      setConfirmPass('');
      setToastMessage('Admin password updated successfully');
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setChangePassSuccess('');
        setToastMessage(null);
      }, 1500);
    } else {
      setChangePassError(res.message);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#070710] text-[#f0f0ff]">
      {/* ── Mobile Overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col w-64 bg-[#0d0d1a] border-r border-[#1e1e3a] transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#1e1e3a]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#FD297B] to-[#FF655B] flex items-center justify-center shadow-lg shadow-pink-500/20">
              <Flame size={22} className="text-white" />
            </div>
            <div>
              <p className="font-extrabold text-lg tracking-wide text-white font-['Outfit']">AMOURA</p>
              <p className="text-xs text-zinc-400 font-medium">Administration Portal</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="px-3 pb-2 text-[11px] font-bold tracking-wider text-zinc-500 uppercase">
            Operations & Moderation
          </p>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-red-600/20 to-pink-600/10 text-white border border-red-500/30 shadow-sm'
                      : 'text-zinc-400 hover:text-white hover:bg-[#141428]'
                  }`
                }
              >
                <Icon size={18} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          <div className="pt-4 pb-2">
            <p className="px-3 pb-2 text-[11px] font-bold tracking-wider text-zinc-500 uppercase">
              System Control
            </p>
          </div>

          <NavLink
            to="/settings"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-red-600/20 to-pink-600/10 text-white border border-red-500/30 shadow-sm'
                  : 'text-zinc-400 hover:text-white hover:bg-[#141428]'
              }`
            }
          >
            <Settings size={18} />
            <span className="flex-1">Settings</span>
            <ChevronRight size={14} className="opacity-30" />
          </NavLink>
          <NavLink
            to="/help"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-red-600/20 to-pink-600/10 text-white border border-red-500/30 shadow-sm'
                  : 'text-zinc-400 hover:text-white hover:bg-[#141428]'
              }`
            }
          >
            <HelpCircle size={18} />
            <span className="flex-1">Help & Docs</span>
            <ChevronRight size={14} className="opacity-30" />
          </NavLink>
        </nav>

        {/* User Footer Profile */}
        <div className="p-3 border-t border-[#1e1e3a]">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#141428] border border-[#1e1e3a]">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-red-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
              OP
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">Ops Admin</p>
              <p className="text-[11px] text-zinc-400 truncate">{getAdminEmail()}</p>
            </div>
            <button
              onClick={() => setShowChangePasswordModal(true)}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-pink-400 hover:bg-zinc-800/50 transition-colors cursor-pointer"
              title="Change Password"
            >
              <KeyRound size={16} />
            </button>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-zinc-800/50 transition-colors cursor-pointer"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header className="flex items-center justify-between gap-4 px-4 sm:px-6 h-16 bg-[#0d0d1a] border-b border-[#1e1e3a] shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-[#141428]"
            >
              <Menu size={20} />
            </button>

            {/* Quick Global Search */}
            <form onSubmit={handleGlobalSearch} className="hidden sm:flex items-center relative w-64 lg:w-72">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder="Search users, transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 transition-colors"
              />
            </form>
          </div>

          <div className="flex items-center gap-3 relative">
            {/* ── Global App Data Mode Switcher (Admin Control) ── */}
            <div className="flex items-center bg-[#141428] p-1 rounded-xl border border-[#1e1e3a] shadow-inner">
              <button
                onClick={() => handleToggleMode('live')}
                disabled={isUpdatingMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer ${
                  systemMode === 'live'
                    ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm shadow-emerald-500/20'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/40'
                }`}
                title="Switch entire app to Live Production Data (Real Cloudflare D1 customers & SquadCo live transactions)"
              >
                <span className={`w-2 h-2 rounded-full ${systemMode === 'live' ? 'bg-emerald-400 shadow-[0_0_8px_#34d399] animate-pulse' : 'bg-zinc-600'}`} />
                <span className="hidden xs:inline">⚡</span>
                <span>Live Data</span>
              </button>

              <button
                onClick={() => handleToggleMode('test')}
                disabled={isUpdatingMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer ${
                  systemMode === 'test'
                    ? 'bg-gradient-to-r from-amber-500/20 to-purple-500/20 text-amber-300 border border-amber-500/40 shadow-sm shadow-amber-500/20'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/40'
                }`}
                title="Switch entire app to Test Sandbox Data (Mock user personas & simulated transactions for QA/demo)"
              >
                <span className={`w-2 h-2 rounded-full ${systemMode === 'test' ? 'bg-amber-400 shadow-[0_0_8px_#fbbf24] animate-pulse' : 'bg-zinc-600'}`} />
                <span className="hidden xs:inline">🧪</span>
                <span>Test Sandbox</span>
              </button>
            </div>

            {/* Cloudflare Zero-Trust Status Badge */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              <ShieldCheck size={14} />
              <span>Edge Verified</span>
            </div>

            {/* Change Password Quick Header Button */}
            <button
              onClick={() => setShowChangePasswordModal(true)}
              className="p-2 rounded-xl bg-[#141428] border border-[#1e1e3a] text-zinc-400 hover:text-pink-400 hover:border-pink-500/40 transition-colors cursor-pointer"
              title="Change Password"
            >
              <KeyRound size={17} />
            </button>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 rounded-xl bg-[#141428] border border-[#1e1e3a] text-zinc-400 hover:text-white transition-colors cursor-pointer"
                title="System Notifications"
              >
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
                )}
              </button>

              {/* Notification Popover Dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] shadow-2xl z-50 overflow-hidden">
                  <div className="flex items-center justify-between p-3.5 border-b border-[#1e1e3a] bg-[#141428]">
                    <span className="text-xs font-bold text-white">System Alerts</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => setUnreadCount(0)}
                        className="text-[11px] text-pink-400 hover:underline cursor-pointer"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-[#1e1e3a] max-h-72 overflow-y-auto">
                    <div
                      onClick={() => { setNotificationsOpen(false); navigate('/kyc'); }}
                      className="p-3 hover:bg-[#141428] transition-colors cursor-pointer flex items-start gap-2.5"
                    >
                      <span className="w-2 h-2 mt-1 rounded-full bg-amber-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">5 Pending KYC Submissions</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">Government NIN & BVN verification queue</p>
                      </div>
                    </div>
                    <div
                      onClick={() => { setNotificationsOpen(false); navigate('/moderation'); }}
                      className="p-3 hover:bg-[#141428] transition-colors cursor-pointer flex items-start gap-2.5"
                    >
                      <span className="w-2 h-2 mt-1 rounded-full bg-pink-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">3 Flagged Images</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">Automated blur moderation pending approval</p>
                      </div>
                    </div>
                    <div
                      onClick={() => { setNotificationsOpen(false); navigate('/payments'); }}
                      className="p-3 hover:bg-[#141428] transition-colors cursor-pointer flex items-start gap-2.5"
                    >
                      <span className="w-2 h-2 mt-1 rounded-full bg-red-400 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">1 Squad Payment Dispute</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">USSD timeout debit alert requires review</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Test Sandbox Alert Banner ── */}
        {systemMode === 'test' && (
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-amber-950/90 via-[#261b0b] to-purple-950/90 border-b border-amber-500/30 text-amber-200 text-xs shrink-0 transition-all">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
              </span>
              <span className="font-extrabold text-[11px] uppercase tracking-wider text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/30 shrink-0">
                🧪 Test Sandbox Active
              </span>
              <span className="text-zinc-300 text-xs truncate font-medium">
                The entire app is serving simulated QA personas & mock metrics. Real customer data is protected.
              </span>
            </div>
            <button
              onClick={() => handleToggleMode('live')}
              disabled={isUpdatingMode}
              className="px-3 py-1 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-extrabold text-xs transition-all shadow-md shrink-0 cursor-pointer"
            >
              Switch to Live Production ⚡
            </button>
          </div>
        )}

        {/* ── Floating Notification Toast ── */}
        {toastMessage && (
          <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-zinc-700 text-white shadow-2xl animate-bounce">
            <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
            <p className="text-xs font-bold">{toastMessage}</p>
          </div>
        )}

        {/* Dynamic Route View */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* ── Global Change Password Modal ── */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl p-6 sm:p-7 relative border border-[#1e1e3a] bg-[#0d0d1a] shadow-2xl">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => {
                setShowChangePasswordModal(false);
                setChangePassError('');
                setChangePassSuccess('');
              }}
              className="absolute top-5 right-5 p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center">
                <KeyRound size={20} className="text-pink-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-['Outfit']">Change Admin Password</h3>
                <p className="text-xs text-zinc-400">Account: {getAdminEmail()}</p>
              </div>
            </div>

            {/* Error & Success Messages */}
            {changePassError && (
              <div className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs bg-red-950/40 border border-red-500/30 text-red-300">
                <AlertCircle size={15} className="mt-0.5 shrink-0" />
                <span>{changePassError}</span>
              </div>
            )}

            {changePassSuccess && (
              <div className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs bg-emerald-950/40 border border-emerald-500/30 text-emerald-300">
                <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                <span>{changePassSuccess}</span>
              </div>
            )}

            {/* Password Form */}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Current Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type={showCurrentPass ? 'text' : 'password'}
                    value={currentPass}
                    onChange={(e) => setCurrentPass(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full pl-10 pr-10 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500 transition-colors"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPass(!showCurrentPass)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                    tabIndex={-1}
                  >
                    {showCurrentPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="w-full pl-10 pr-10 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500 transition-colors"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                    tabIndex={-1}
                  >
                    {showNewPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full pl-10 pr-4 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3">
                <button
                  type="button"
                  onClick={() => setShowChangePasswordModal(false)}
                  className="px-4 py-2.5 text-xs font-semibold text-zinc-400 hover:text-white transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changePassLoading}
                  className="px-5 py-2.5 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white hover:opacity-90 transition-opacity flex items-center gap-2 cursor-pointer shadow-lg shadow-pink-600/20"
                >
                  {changePassLoading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin text-white" />
                      Saving...
                    </>
                  ) : (
                    'Save New Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
